import { createReducer, on } from '@ngrx/store';
import { Departures } from 'app/core/enums/common.enum';
import * as TotalGrossActions from '../total-gross/total-gross.actions';
import * as ParkingSalesActions from './parking-sales.actions';
import { ParkingSale } from './parking-sales.models';

// ----------------------------------------------------------------------
// @ States
// ----------------------------------------------------------------------

export interface ParkingSalesState {
    sales: ParkingSale[];
    sale: ParkingSale | null;
    commissions: { commissions: any[]; totals: any };
    isLoading: boolean;
    isLoadingCommissions: boolean;
    error: string | null;
}

// ----------------------------------------------------------------------
// @ Initial States
// ----------------------------------------------------------------------

export const initialParkingSalesState: ParkingSalesState = {
    sales: [],
    sale: null,
    commissions: { commissions: [], totals: {} },
    isLoading: false,
    isLoadingCommissions: false,
    error: null,
};

// ----------------------------------------------------------------------
// @ Reducers
// ----------------------------------------------------------------------

export const parkingSalesReducer = createReducer(
    initialParkingSalesState,

    // On Load ParkingSales
    on(ParkingSalesActions.loadParkingSales, (state) => ({
        ...state,
        isLoading: true,
    })),
    on(ParkingSalesActions.loadParkingSalesSuccess, (state, { data }) => ({
        ...state,
        isLoading: false,
        sales: data,
    })),
    on(ParkingSalesActions.loadParkingSalesFailure, (state, { error }) => ({
        ...state,
        isLoading: false,
        error: error,
    })),

    // On Clear
    on(ParkingSalesActions.clearParkingSales, (state) => ({
        ...state,
        sales: [],
        sale: null,
        commissions: { commissions: [], totals: {} },
        isLoading: false,
        isLoadingCommissions: false,
        error: null,
    })),

    // On Load Single ParkingSale
    on(ParkingSalesActions.loadParkingSale, (state) => ({
        ...state,
        isLoading: true,
    })),
    on(ParkingSalesActions.loadParkingSaleSuccess, (state, { data }) => ({
        ...state,
        isLoading: false,
        sale: data,
    })),
    on(ParkingSalesActions.loadParkingSaleFailure, (state, { error }) => ({
        ...state,
        isLoading: false,
        error: error,
    })),

    // On Create
    on(ParkingSalesActions.createParkingSale, (state) => ({
        ...state,
        isLoading: true,
    })),
    on(ParkingSalesActions.createParkingSaleSuccess, (state, { sale }) => ({
        ...state,
        isLoading: false,
        sales: ((): ParkingSale[] => {
            const sales = state.sales || [];
            let updateSales = [];
            sales.some((s) => s.location.id === sale.location.id)
                ? (updateSales = sales.map((_sale: ParkingSale) =>
                      _sale.location.id === sale.location.id
                          ? {
                                ..._sale,
                                children: [..._sale.children, ...sale.children],
                                totals: sale.totals,
                            }
                          : _sale
                  ))
                : (updateSales = [...sales, sale]);
            return updateSales;
        })(),
    })),
    on(ParkingSalesActions.createParkingSaleFailure, (state, { error }) => ({
        ...state,
        isLoading: false,
        error: error,
    })),

    // On Update
    on(ParkingSalesActions.updateParkingSale, (state) => ({
        ...state,
        isLoading: true,
    })),
    on(ParkingSalesActions.updateParkingSaleSuccess, (state, { data }) => ({
        ...state,
        isLoading: false,
        sales: state.sales.map((s) =>
            s.location.id === data.location.id
                ? {
                      ...s,
                      children: s.children.map((child) =>
                          child.saleId === data.children[0].saleId
                              ? data.children[0]
                              : child
                      ),
                      totals: data.totals,
                  }
                : s
        ),
    })),
    on(ParkingSalesActions.updateParkingSaleFailure, (state, { error }) => ({
        ...state,
        isLoading: false,
        error: error,
    })),

    // On Update Location Gross
    on(
        TotalGrossActions.updateTotalGrossSuccess,
        (state, { data, departure }) => ({
            ...state,
            isLoading: false,
            sales: state.sales.map((s) =>
                s.location.id === data.location_id &&
                departure === Departures.PARKING
                    ? {
                          ...s,
                          cash: data.cash,
                          credit_card: data.credit_card,
                          total_money: data.total_gross,
                          totals_local: {
                              cash: data.total_local.cash,
                              credit_card: data.total_local.credit_card,
                              count: s.totals_local.count,
                              amount: data.total_gross,
                          },
                      }
                    : s
            ),
        })
    ),

    // On Delete
    on(ParkingSalesActions.deleteParkingSale, (state) => ({
        ...state,
        isLoading: true,
    })),
    on(
        ParkingSalesActions.deleteParkingSaleSuccess,
        (state, { deletedSales, remainingSales }) => ({
            ...state,
            isLoading: false,
            sales: (() => {
                if (remainingSales.length === 0) {
                    return state.sales.filter(
                        (sales: ParkingSale) =>
                            !sales.children.some(
                                (child) => child.saleId === deletedSales[0]
                            )
                    );
                } else {
                    console.warn(
                        'Remaining sales after deletion:',
                        remainingSales
                    );
                    return state.sales;
                }
            })(),
        })
    ),
    on(ParkingSalesActions.deleteParkingSaleFailure, (state, { error }) => ({
        ...state,
        isLoading: false,
        error: error,
    })),

    // On Delete Sale Item
    on(ParkingSalesActions.deleteParkingSaleItem, (state) => ({
        ...state,
        isLoading: true,
    })),
    on(
        ParkingSalesActions.deleteParkingSaleItemSuccess,
        (state, { deletedSaleItem, totals }) => ({
            ...state,
            isLoading: false,
            sales: state.sales.map((_sale: ParkingSale) =>
                _sale.children.some((child) => child.saleId === deletedSaleItem)
                    ? {
                          ..._sale,
                          children: _sale.children.filter(
                              (child) => child.saleId !== deletedSaleItem
                          ),
                          totals: totals,
                      }
                    : _sale
            ),
        })
    ),
    on(
        ParkingSalesActions.deleteParkingSaleItemFailure,
        (state, { error }) => ({
            ...state,
            isLoading: false,
            error: error,
        })
    )
);
